import {
  PROXY,
  YES_NO,
  applicantSection,
  businessSection,
  deMinimisAttachments,
  feeInfo,
  otherAttachments,
  penaltyDeclaration,
  proxyAttachments,
  replySection,
} from "modules/ebiForms/common";
import { SURETY, expensesField, securityField } from "modules/ebiForms/definitions/sharedFamilyLabour";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * H7. Wnioski pracodawców o wsparcie zatrudnienia — ustawa z 20.03.2025 r. o rynku pracy i służbach zatrudnienia:
 * refundacja kosztów wyposażenia lub doposażenia stanowiska pracy (art. 154–159; treść wniosku i oświadczenia: § 6
 * rozporządzenia MRPiPS z 21.11.2025 r., Dz.U. poz. 1645), prace interwencyjne i roboty publiczne (art. 135–138; treść
 * wniosku: art. 138 ust. 4), staż (art. 114–115; treść wniosku i program: § 2 i § 5 rozporządzenia MRPiPS z 30.10.2025 r.,
 * Dz.U. poz. 1536). Teksty aktów z api.sejm.gov.pl pobrane 2026-10-22; klauzula karna: art. 138 ust. 5 i art. 149 ust. 2
 * w zw. z art. 159. Brak wzorów ogólnopolskich; część „staż” jak wzór PUP Wrocław —
 * https://wroclaw.praca.gov.pl/documents/d/powiatowy-urzad-pracy-we-wroclawiu/wniosek-stazowy-nowy-2025r- (pobrany
 * 2026-10-22); wzoru UP m.st. Warszawy (refundacja, DOCX) nie pobierano. Bony stażowy, zatrudnieniowy i szkoleniowy nie
 * występują w ustawie z 2025 r. — nie są wariantem, tylko informacją. Oświadczenia pozostałych osób reprezentujących
 * pracodawcę — skany (EBI podpisuje tylko składający).
 */
const REFUND: EbiCondition = { field: "instrument", equals: "refundacja" };
const PUBLIC_WORKS: EbiCondition = { field: "instrument", equals: "robotyPubliczne" };
const INTERNSHIP: EbiCondition = { field: "instrument", equals: "staz" };
const WORKS: EbiCondition = { field: "instrument", in: ["praceInterwencyjne", "robotyPubliczne"] };
/** Formy pomocy z oświadczeniami ustawowymi pod rygorem odpowiedzialności karnej i pomocą de minimis. */
const AID: EbiCondition = { field: "instrument", in: ["refundacja", "praceInterwencyjne", "robotyPubliczne"] };
const FARMER: EbiCondition = { all: [REFUND, { field: "producentRolny", equals: true }] };
const NOT_FARMER: EbiCondition = { all: [REFUND, { not: { field: "producentRolny", equals: true } }] };
const OTHER_EMPLOYER: EbiCondition = { all: [PUBLIC_WORKS, { field: "innyPracodawca", equals: "tak" }] };
const NAMED_INTERN: EbiCondition = { all: [INTERNSHIP, { field: "wskazanyBezrobotny", equals: "tak" }] };

const H7: IEbiFormDefinition = {
  code: "H7",
  version: 1,
  title: "Wniosek pracodawcy o wsparcie zatrudnienia (refundacja stanowiska pracy, prace interwencyjne, roboty publiczne, staż)",
  category: EEbiFormCategory.Labour,
  office: EEbiFormOffice.CountyUnit,
  description:
    "Refundacja kosztów wyposażenia lub doposażenia stanowiska pracy, organizacja prac interwencyjnych, robót publicznych albo stażu dla bezrobotnych.",
  keywords: [
    "pracodawca",
    "refundacja",
    "doposażenie stanowiska pracy",
    "prace interwencyjne",
    "roboty publiczne",
    "staż",
    "PUP",
    "Fundusz Pracy",
    "de minimis",
  ],
  organ: "Starosta — Powiatowy Urząd Pracy (refundacja: według miejsca wykonywania działalności lub pracy; prace interwencyjne: wybrany PUP; roboty publiczne: według miejsca wykonywania robót)",
  legalBasis:
    "art. 114–115, art. 135–138 i art. 154–159 ustawy z dnia 20 marca 2025 r. o rynku pracy i służbach zatrudnienia; § 6–7 rozporządzenia Ministra Rodziny, Pracy i Polityki Społecznej z dnia 21 listopada 2025 r. w sprawie wniosków i realizacji umów o dofinansowanie podjęcia działalności gospodarczej oraz o refundację kosztów wyposażenia lub doposażenia stanowiska pracy; § 2–5 rozporządzenia Ministra Rodziny, Pracy i Polityki Społecznej z dnia 30 października 2025 r. w sprawie szczegółowego sposobu i trybu organizowania stażu dla bezrobotnych",
  request: "Wnoszę o zawarcie umowy w sprawie formy wsparcia wskazanej we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty; pełnomocnictwo — 17 zł.",
  deadlineLabel: "do 30 dni",
  deadline:
    "Refundacja i staż — informacja o rozpatrzeniu wniosku w terminie 30 dni od złożenia kompletnego wniosku (§ 7 ust. 6 rozporządzenia o refundacji, § 3 ust. 1 rozporządzenia o stażu).",
  variantField: "instrument",
  variants: {
    refundacja: { title: "Wniosek o refundację kosztów wyposażenia lub doposażenia stanowiska pracy" },
    praceInterwencyjne: { title: "Wniosek o organizowanie prac interwencyjnych" },
    robotyPubliczne: { title: "Wniosek o organizowanie robót publicznych" },
    staz: { title: "Wniosek o zawarcie umowy o zorganizowanie stażu" },
  },
  sections: [
    {
      code: "formaWsparcia",
      title: "Forma wsparcia",
      fields: [
        {
          code: "instrument",
          type: "radio",
          label: "Wnoszę o",
          required: true,
          options: [
            { value: "refundacja", label: "refundację kosztów wyposażenia lub doposażenia stanowiska pracy dla skierowanego bezrobotnego" },
            { value: "praceInterwencyjne", label: "organizowanie prac interwencyjnych" },
            { value: "robotyPubliczne", label: "organizowanie robót publicznych" },
            { value: "staz", label: "zawarcie umowy o zorganizowanie stażu dla bezrobotnych" },
          ],
        },
        feeInfo(
          "infoNabor",
          "PUP rozpatruje wnioski pracodawców w miarę posiadanych środków, zwykle w ogłaszanych naborach — sprawdź ogłoszenie i regulamin urzędu. Bony stażowy, zatrudnieniowy i szkoleniowy nie występują w ustawie z dnia 20 marca 2025 r. o rynku pracy i służbach zatrudnienia (pozostał bon na zasiedlenie, o który wnioskuje bezrobotny).",
        ),
      ],
    },
    applicantSection("Osoba składająca wniosek w imieniu pracodawcy"),
    businessSection("Pracodawca (organizator)"),
    {
      code: "pracodawcaUzupelnienie",
      title: "Dane pracodawcy — uzupełnienie",
      fields: [
        {
          code: "wImieniu",
          type: "radio",
          label: "Składam wniosek",
          required: true,
          defaultValue: "reprezentant",
          options: [
            { value: "reprezentant", label: "jako osoba uprawniona do reprezentowania pracodawcy (KRS, CEIDG lub inny rejestr)" },
            { value: "pelnomocnik", label: "jako pełnomocnik pracodawcy" },
          ],
        },
        {
          code: "pelnomocnictwoZwolnione",
          type: "checkbox",
          label:
            "Jestem małżonkiem, wstępnym, zstępnym lub rodzeństwem mocodawcy — pełnomocnictwo jest zwolnione z opłaty skarbowej",
          visibleWhen: PROXY,
        },
        {
          code: "formaPrawna",
          type: "text",
          label: "Forma organizacyjno-prawna prowadzonej działalności",
          required: true,
          width: "half",
          maxLength: 200,
          placeholder: "np. spółka z o.o., jednoosobowa działalność gospodarcza",
        },
        { code: "pkdPracodawcy", type: "text", label: "Symbol podklasy PKD", width: "half", maxLength: 10, placeholder: "np. 43.32.Z" },
        {
          code: "miejsceDzialalnosci",
          type: "text",
          label: "Miejsce prowadzenia działalności (jeśli inne niż siedziba)",
          maxLength: 300,
        },
        {
          code: "adresDoreczenElektronicznych",
          type: "text",
          label: "Adres do doręczeń elektronicznych (jeśli pracodawca posiada)",
          maxLength: 100,
          placeholder: "AE:PL-00000-00000-00000-00",
        },
        { code: "dataRozpoczecia", type: "date", label: "Data rozpoczęcia prowadzenia działalności", required: true, visibleWhen: REFUND, width: "half" },
        {
          code: "liczbaPracownikow",
          type: "number",
          label: "Liczba pracowników w przeliczeniu na pełny wymiar czasu pracy",
          required: true,
          visibleWhen: INTERNSHIP,
          width: "half",
          decimals: 2,
          min: 0,
        },
        { code: "opisDzialalnosci", type: "textarea", label: "Opis prowadzonej działalności", required: true, visibleWhen: REFUND, maxLength: 3000 },
        {
          code: "producentRolny",
          type: "checkbox",
          label: "Wnioskodawca jest producentem rolnym (art. 154 ust. 1 pkt 3 ustawy)",
          visibleWhen: REFUND,
        },
      ],
    },
    {
      code: "refundacja",
      title: "Refundacja kosztów wyposażenia lub doposażenia stanowiska pracy",
      visibleWhen: REFUND,
      fields: [
        { code: "liczbaStanowisk", type: "number", label: "Liczba wyposażanych lub doposażanych stanowisk pracy", required: true, width: "third", min: 1, max: 100 },
        {
          code: "wymiarCzasu",
          type: "text",
          label: "Wymiar czasu pracy zatrudnianych skierowanych osób",
          required: true,
          width: "twoThirds",
          maxLength: 100,
          placeholder: "np. pełny etat",
        },
        { code: "adresPracy", type: "text", label: "Adres miejsca wykonywania pracy przez skierowanego bezrobotnego", required: true, maxLength: 300 },
        { code: "rodzajPracy", type: "textarea", label: "Rodzaj pracy i opis zadań na refundowanym stanowisku", required: true, maxLength: 3000 },
        {
          code: "kwalifikacjeStanowisko",
          type: "textarea",
          label: "Wymagane kwalifikacje, umiejętności i doświadczenie zawodowe",
          required: true,
          maxLength: 2000,
        },
        expensesField({
          code: "wydatkiStanowisko",
          label: "Specyfikacja i kalkulacja wydatków na wyposażenie lub doposażenie stanowisk pracy",
          hint: "Środki trwałe, urządzenia, maszyny, wartości niematerialne i prawne, w tym wyposażenie zapewniające zgodność stanowiska z przepisami BHP i wymaganiami ergonomii (§ 6 ust. 3 pkt 6–7 rozporządzenia).",
          withPosition: true,
        }),
        {
          code: "kwotaRefundacji",
          type: "number",
          label: "Wnioskowana kwota refundacji [zł]",
          required: true,
          width: "half",
          decimals: 2,
          min: 0,
          hint: "Do 4-krotności, a wyjątkowo do 6-krotności przeciętnego wynagrodzenia (art. 154 ust. 4 ustawy).",
        },
        securityField(REFUND),
      ],
    },
    {
      code: "praceRoboty",
      title: "Prace interwencyjne / roboty publiczne",
      visibleWhen: WORKS,
      fields: [
        { code: "liczbaBezrobotnych", type: "number", label: "Liczba bezrobotnych proponowanych do zatrudnienia", required: true, width: "third", min: 1, max: 500 },
        {
          code: "okresZatrudnienia",
          type: "text",
          label: "Okres zatrudnienia",
          required: true,
          width: "twoThirds",
          maxLength: 100,
          hint: "Prace interwencyjne: od 3 do 12 miesięcy (art. 135 ust. 1); roboty publiczne: do 6 miesięcy (art. 136 ust. 1).",
        },
        { code: "miejscePrac", type: "text", label: "Miejsce wykonywania prac", required: true, maxLength: 300 },
        { code: "rodzajPrac", type: "textarea", label: "Rodzaj prac", required: true, maxLength: 3000 },
        { code: "kwalifikacjePrac", type: "textarea", label: "Niezbędne lub pożądane kwalifikacje", required: true, maxLength: 2000 },
        {
          code: "wynagrodzenie",
          type: "number",
          label: "Proponowane wynagrodzenie skierowanych bezrobotnych [zł brutto miesięcznie]",
          required: true,
          width: "half",
          decimals: 2,
          min: 0,
        },
        {
          code: "wnioskowanaRefundacja",
          type: "number",
          label: "Wnioskowana wysokość refundowanych kosztów wynagrodzenia [zł]",
          required: true,
          width: "half",
          decimals: 2,
          min: 0,
          hint: "Prace interwencyjne: nie więcej niż minimalne wynagrodzenie za każdego bezrobotnego; roboty publiczne: do 50% przeciętnego wynagrodzenia na osobę w przeliczeniu na pełny etat (art. 135–136 ustawy).",
        },
        {
          code: "innyPracodawca",
          type: "radio",
          label: "Czy pracodawcą skierowanych bezrobotnych będzie inny podmiot niż organizator robót publicznych?",
          required: true,
          visibleWhen: PUBLIC_WORKS,
          options: YES_NO,
        },
        { code: "innyPracodawcaNazwa", type: "text", label: "Nazwa pracodawcy", required: true, visibleWhen: OTHER_EMPLOYER, maxLength: 300 },
        {
          code: "innyPracodawcaAdres",
          type: "text",
          label: "Adres siedziby i miejsce prowadzenia działalności pracodawcy",
          required: true,
          visibleWhen: OTHER_EMPLOYER,
          maxLength: 300,
        },
      ],
    },
    {
      code: "staz",
      title: "Organizacja stażu",
      visibleWhen: INTERNSHIP,
      fields: [
        { code: "liczbaMiejsc", type: "number", label: "Liczba miejsc stażu", required: true, width: "third", min: 1, max: 100 },
        {
          code: "okresStazu",
          type: "number",
          label: "Proponowany okres stażu [miesiące]",
          required: true,
          width: "third",
          min: 3,
          max: 12,
          hint: "Od 3 do 6 miesięcy, a w przypadkach z art. 119 ust. 1 ustawy — do 12 miesięcy (art. 114 ust. 3).",
        },
        {
          code: "zawodNazwa",
          type: "text",
          label: "Nazwa zawodu lub specjalności (klasyfikacja zawodów i specjalności)",
          required: true,
          width: "twoThirds",
          maxLength: 200,
        },
        { code: "zawodKod", type: "text", label: "Kod zawodu (6 cyfr)", required: true, width: "third", maxLength: 6, placeholder: "np. 411004" },
        { code: "stanowiskoStazu", type: "text", label: "Nazwa stanowiska pracy", required: true, width: "half", maxLength: 200 },
        { code: "komorkaStazu", type: "text", label: "Komórka organizacyjna (jeśli występuje)", width: "half", maxLength: 200 },
        { code: "miejsceStazu", type: "text", label: "Miejsce odbywania stażu (adres)", required: true, maxLength: 300 },
        {
          code: "czasStazu",
          type: "textarea",
          label: "System czasu pracy, wymiar czasu stażu, proponowane godziny i rozkład czasu pracy",
          required: true,
          maxLength: 1000,
          placeholder: "np. system podstawowy, 8 godzin dziennie, poniedziałek–piątek 8:00–16:00",
        },
        {
          code: "formaStazu",
          type: "radio",
          label: "Forma stażu",
          required: true,
          options: [
            { value: "stacjonarna", label: "stacjonarna" },
            { value: "zdalna", label: "z możliwością realizacji w formie zdalnej (w całości lub części)" },
          ],
        },
        {
          code: "stazZdalny",
          type: "textarea",
          label: "Wymiar stażu w miejscu wskazanym przez stażystę oraz zasady stażu zdalnego (porozumiewanie się, potwierdzanie obecności)",
          required: true,
          visibleWhen: { all: [INTERNSHIP, { field: "formaStazu", equals: "zdalna" }] },
          maxLength: 2000,
        },
        {
          code: "zadaniaStazu",
          type: "textarea",
          label: "Program stażu — zakres i opis zadań zawodowych wykonywanych przez bezrobotnego",
          required: true,
          maxLength: 5000,
        },
        {
          code: "wiedzaStazu",
          type: "textarea",
          label: "Program stażu — zakres wiedzy i umiejętności zawodowych do nabycia",
          required: true,
          maxLength: 3000,
        },
        {
          code: "wymaganiaStazu",
          type: "textarea",
          label: "Wymagania wobec bezrobotnego — predyspozycje psychofizyczne i zdrowotne, poziom wykształcenia, minimalne kwalifikacje",
          required: true,
          maxLength: 2000,
        },
        {
          code: "dostepnoscStazu",
          type: "textarea",
          label: "Możliwość odbycia stażu przez osoby z niepełnosprawnością i dostępność stanowiska dla tych osób",
          required: true,
          maxLength: 1000,
        },
        {
          code: "opiekunowieStazu",
          type: "repeat",
          label: "Opiekunowie stażystów",
          itemLabel: "Opiekun",
          required: true,
          min: 1,
          max: 10,
          fields: [
            { code: "imieNazwisko", type: "text", label: "Imię i nazwisko", required: true, width: "half", maxLength: 200 },
            { code: "stanowisko", type: "text", label: "Zajmowane stanowisko", required: true, width: "half", maxLength: 200 },
          ],
        },
        {
          code: "wskazanyBezrobotny",
          type: "radio",
          label: "Czy wskazujesz bezrobotnego, którego chcesz przyjąć na staż?",
          required: true,
          options: YES_NO,
        },
        {
          code: "wskazanyImieNazwisko",
          type: "text",
          label: "Imię i nazwisko bezrobotnego",
          required: true,
          visibleWhen: NAMED_INTERN,
          width: "half",
          maxLength: 200,
        },
        { code: "wskazanyDataUrodzenia", type: "date", label: "Data urodzenia bezrobotnego", required: true, visibleWhen: NAMED_INTERN, width: "half" },
        {
          code: "oswWskazany",
          type: "declaration",
          label:
            "Oświadczam, że wskazany bezrobotny nie odbywał u organizatora stażu, nie był u niego zatrudniony, w tym jako młodociany pracownik w celu przygotowania zawodowego, ani nie wykonywał u niego innej pracy zarobkowej w okresie ostatnich 24 miesięcy.",
          required: true,
          visibleWhen: NAMED_INTERN,
        },
        {
          code: "zobowiazanieBadania",
          type: "declaration",
          label: "Zobowiązuję się skierować bezrobotnego, na własny koszt, na wstępne badania lekarskie przed powierzeniem mu wykonywania zadań przewidzianych programem stażu.",
          required: true,
        },
      ],
    },
    {
      code: "oswiadczeniaPracodawcy",
      title: "Oświadczenia",
      fields: [
        {
          code: "oswNiekaralnoscRefundacja",
          type: "declaration",
          label:
            "Oświadczam, że ani wnioskodawca, ani ja jako osoba go reprezentująca nie zostaliśmy w okresie ostatnich 2 lat prawomocnie skazani za przestępstwo składania fałszywych zeznań lub oświadczeń, przestępstwo przeciwko wiarygodności dokumentów lub przeciwko obrotowi gospodarczemu i interesom majątkowym w obrocie cywilnoprawnym na podstawie Kodeksu karnego, za przestępstwo skarbowe na podstawie Kodeksu karnego skarbowego lub za odpowiedni czyn zabroniony określony w przepisach prawa obcego.",
          required: true,
          visibleWhen: REFUND,
        },
        {
          code: "oswNiekaralnoscPrace",
          type: "declaration",
          label:
            "Oświadczam, że jako osoba reprezentująca podmiot ubiegający się o organizację prac interwencyjnych lub robót publicznych nie zostałam/zostałem w okresie ostatnich 2 lat prawomocnie skazana/skazany za przestępstwo składania fałszywych zeznań lub oświadczeń, przestępstwo przeciwko wiarygodności dokumentów lub przeciwko obrotowi gospodarczemu i interesom majątkowym w obrocie cywilnoprawnym, przestępstwo przeciwko prawom osób wykonujących pracę zarobkową na podstawie Kodeksu karnego, przestępstwo skarbowe na podstawie Kodeksu karnego skarbowego lub za odpowiedni czyn zabroniony określony w przepisach prawa obcego.",
          required: true,
          visibleWhen: WORKS,
        },
        {
          code: "oswNiezaleganie",
          type: "declaration",
          label:
            "Oświadczam, że na dzień złożenia wniosku wnioskodawca nie zalega z wypłacaniem wynagrodzeń pracownikom, z opłacaniem należnych składek na ubezpieczenia społeczne, ubezpieczenie zdrowotne, Fundusz Pracy, Fundusz Gwarantowanych Świadczeń Pracowniczych, Fundusz Solidarnościowy i Fundusz Emerytur Pomostowych, z wpłatami na Państwowy Fundusz Rehabilitacji Osób Niepełnosprawnych, z opłacaniem należnych składek na ubezpieczenie społeczne rolników lub na ubezpieczenie zdrowotne oraz z opłacaniem innych danin publicznych.",
          required: true,
        },
        {
          code: "osw6Miesiecy",
          type: "declaration",
          label:
            "Oświadczam, że wnioskodawca wykonuje działalność gospodarczą (niepubliczne przedszkole lub szkoła — działalność na podstawie ustawy — Prawo oświatowe) przez ostatnie 6 miesięcy.",
          required: true,
          visibleWhen: NOT_FARMER,
        },
        {
          code: "oswGospodarstwo",
          type: "declaration",
          label: "Oświadczam, że wnioskodawca przez ostatnie 6 miesięcy posiadał gospodarstwo rolne lub prowadził dział specjalny produkcji rolnej.",
          required: true,
          visibleWhen: FARMER,
        },
        {
          code: "oswPracownikRolny",
          type: "declaration",
          label:
            "Oświadczam, że wnioskodawca w okresie ostatnich 6 miesięcy zatrudniał w każdym miesiącu co najmniej jednego pracownika w pełnym wymiarze czasu pracy.",
          required: true,
          visibleWhen: FARMER,
        },
        {
          code: "oswStanZatrudnienia",
          type: "declaration",
          label:
            "Oświadczam, że w okresie ostatnich 6 miesięcy wnioskodawca nie zmniejszył wymiaru czasu pracy i stanu zatrudnienia pracowników z przyczyn dotyczących zakładu pracy, a w przypadku ich zmniejszenia z innych przyczyn — uzupełnił wymiar czasu pracy lub stan zatrudnienia.",
          required: true,
          visibleWhen: REFUND,
        },
        {
          code: "oswZobowiazania",
          type: "declaration",
          label: "Oświadczam, że wnioskodawca nie posiada nieuregulowanych w terminie zobowiązań cywilnoprawnych.",
          required: true,
          visibleWhen: REFUND,
        },
        {
          code: "inneOsoby",
          type: "radio",
          label: "Czy wnioskodawcę reprezentują lub nim zarządzają także inne osoby niż składający wniosek?",
          required: true,
          visibleWhen: AID,
          options: YES_NO,
          hint: "Oświadczenie o niekaralności składa każda z tych osób (art. 138 ust. 6 ustawy, § 6 ust. 5 pkt 1 rozporządzenia) — dołącz skany podpisanych oświadczeń.",
        },
        {
          code: "deMinimis",
          type: "radio",
          label: "Czy wnioskodawca jest beneficjentem pomocy de minimis (prowadzi działalność gospodarczą)?",
          required: true,
          visibleWhen: AID,
          options: YES_NO,
          hint: "Nie dotyczy m.in. niepublicznych przedszkoli i szkół oraz żłobków i klubów dziecięcych w zakresie zadań ustawowych (§ 6 ust. 6 rozporządzenia).",
        },
        { ...penaltyDeclaration(), visibleWhen: AID },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "oswiadczeniaInnychOsob",
      label: "Oświadczenia pozostałych osób reprezentujących lub zarządzających wnioskodawcą o niekaralności",
      hint: "Skany podpisanych oświadczeń z klauzulą: „Jestem świadomy odpowiedzialności karnej za złożenie fałszywego oświadczenia.”",
      visibleWhen: { all: [AID, { field: "inneOsoby", equals: "tak" }] },
      required: true,
      multiple: true,
    },
    ...deMinimisAttachments({ all: [AID, { field: "deMinimis", equals: "tak" }] }),
    {
      code: "oswiadczeniaPoreczycieli",
      label: "Oświadczenia poręczycieli o dochodach i zobowiązaniach finansowych",
      hint: "Na druku PUP (§ 10 ust. 4 rozporządzenia o refundacji).",
      visibleWhen: { all: [REFUND, SURETY] },
      multiple: true,
    },
    {
      code: "programStazuDruk",
      label: "Program stażu na formularzu PUP",
      hint: "Jeśli urząd wymaga programu na własnym druku — elementy programu podajesz też w formularzu.",
      visibleWhen: INTERNSHIP,
    },
    {
      code: "dokumentyRejestrowe",
      label: "Umowa spółki cywilnej lub inny dokument potwierdzający formę prawną",
      hint: "Gdy forma prawna nie wynika z KRS ani CEIDG (praktyka PUP).",
    },
    {
      code: "zgloszenieOferty",
      label: "Zgłoszenie oferty pracy",
      hint: "Praktyka PUP — m.in. przy deklaracji zatrudnienia po stażu.",
      visibleWhen: { any: [WORKS, INTERNSHIP] },
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default H7;
